import { getClientEnv } from '@lqa-frontend/shared';
import type { InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import { HTTP_STATUS } from './domain';
import { endpoints } from './endpoints';

export const AUTH_LOCAL_STORAGE_KEY = 'control-hub-app:core:api:key:auth';

const coreApiClient = axios.create({
	baseURL: getClientEnv('APP_URL_CORE_API'),
	headers: {
		'Content-Type': 'application/json',
	},
});

coreApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
	if (typeof window === 'undefined') return config;

	const accessToken = localStorage.getItem(AUTH_LOCAL_STORAGE_KEY);
	if (
		accessToken !== null &&
		accessToken !== 'undefined' &&
		accessToken !== 'null'
	) {
		config.headers.Authorization = `Bearer ${JSON.parse(accessToken)}`;
	}

	return config;
});

// Function to check if a string is a date in the ISO 8601 format
function isISO8601Date(value: string): boolean {
	const regex = /^\d{4}-\d{2}-\d{2}$/;
	return regex.test(value);
}

export function isISO8601DateTime(value: string): boolean {
	const regex =
		/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?([+-]\d{2}:\d{2}|Z)?$/;
	return regex.test(value);
}

// Recursive function to transform all dates
// eslint-disable-next-line ts/no-explicit-any
function transformDates(data: any): any {
	if (Array.isArray(data)) {
		// If it's an array, map over its elements
		return data.map(transformDates);
	} else if (typeof data === 'object' && data !== null) {
		// If it's an object, transform each key-value pair
		return Object.fromEntries(
			Object.entries(data).map(([key, value]) => [key, transformDates(value)])
		);
	} else if (typeof data === 'string') {
		// If it's a string, check if it's an ISO 8601 date or datetime and transform
		if (isISO8601DateTime(data) || isISO8601Date(data)) {
			return new Date(data); // Transform to Date object
		}

		return data; // Leave unchanged if not a date
	}
	return data; // Leave unchanged if not a string, array, or object
}

coreApiClient.interceptors.response.use(
	(response) => {
		if (response.data instanceof Blob) {
			return response;
		}
		response.data = transformDates(response.data);
		return response;
	},
	(error) => {
		const { response } = error;
		if (
			response?.status === HTTP_STATUS.UNAUTHORIZED &&
			response.config.url !== endpoints.authentication.login
		) {
			localStorage.removeItem(AUTH_LOCAL_STORAGE_KEY);
		}

		return Promise.reject(error);
	}
);

export { coreApiClient };
